import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import type { RetrieveResourceDetailOutput } from '@protocols/use-cases';

import { ResourceDraftData } from './staged-resources.state';

/**
 * Mask-only crop offsets. Carried in state as plain numbers (the legacy editor
 * round-tripped them through a JSON form control) and folded into `metadata`
 * only when the resource is a mask — see `draft$`.
 */
export interface ResourceEditorCoordinates {
  offsetXmin: number;
  offsetYmin: number;
  offsetXmax: number;
  offsetYmax: number;
}

const DEFAULT_COORDINATES: ResourceEditorCoordinates = {
  offsetXmin: 0.31,
  offsetYmin: 0.31,
  offsetXmax: 0.65,
  offsetYmax: 0.64,
};

/**
 * The persistable projection of an editing session. The staging ledger owns the
 * `nestedResourcesCount` / `pending` bookkeeping, so the editor never produces
 * them — everything else of `ResourceDraftData` is editor-authored here.
 */
export type ResourceEditorDraft = Omit<ResourceDraftData, 'nestedResourcesCount' | 'pending'>;

interface ResourceEditorStateModel {
  /* Persisted id; null = create. */
  id: string | null;
  /* Ledger/tree key: persisted id or placeholder uuid. */
  containerId: string | null;
  name: string;
  type: string | null;
  /* Editing an existing response locks the type select. */
  typeLocked: boolean;
  version: number;
  description: string;
  /* Stored path, or the picked file's name until the upload resolves. */
  value: string;
  /* Picked file awaiting lazy upload. */
  file: File | null;
  /* metadata.label */
  label: string;
  coordinates: ResourceEditorCoordinates;
  displayName: Record<string, string>;
  loading: boolean;
  saving: boolean;
  /*
   * Editor-owned i18n key for UI-driven errors. Effects never write here — they
   * push typed exceptions into the slots below and the facade dispatches them.
   */
  error: string | null;
  /* Raw exception from the most recent load failure. */
  loadException: Error | null;
  /* Raw exception from the most recent save failure. */
  saveException: Error | null;
  /*
   * As-loaded snapshot of the resource being edited, in ledger-row shape. The
   * dialog flow stages it as the `original` half of a resource override (the
   * page flow consolidates immediately and keeps its draft-as-original
   * shortcut). `null` in create mode.
   */
  loadedOriginal: ResourceDraftData | null;
  /*
   * Signature of the session as seeded (see
   * `ResourceEditorEffects.currentSignature$`). `null` until a seed completes —
   * `hasChanges$` stays false, so a cancel before/without a successful seed
   * closes silently.
   */
  baselineSignature: string | null;
}

const INITIAL_STATE: ResourceEditorStateModel = {
  id: null,
  containerId: null,
  name: '',
  type: null,
  typeLocked: false,
  version: 1,
  description: '',
  value: '',
  file: null,
  label: '',
  coordinates: { ...DEFAULT_COORDINATES },
  displayName: {},
  loading: false,
  saving: false,
  error: null,
  loadException: null,
  saveException: null,
  loadedOriginal: null,
  baselineSignature: null,
};

@Injectable()
export class ResourceEditorState extends ComponentStore<ResourceEditorStateModel> {
  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly id$ = this.select((s) => s.id);
  public readonly containerId$ = this.select((s) => s.containerId);
  public readonly name$ = this.select((s) => s.name);
  public readonly type$ = this.select((s) => s.type);
  public readonly typeLocked$ = this.select((s) => s.typeLocked);
  public readonly version$ = this.select((s) => s.version);
  public readonly description$ = this.select((s) => s.description);
  public readonly value$ = this.select((s) => s.value);
  public readonly file$ = this.select((s) => s.file);
  public readonly label$ = this.select((s) => s.label);
  public readonly coordinates$ = this.select((s) => s.coordinates);
  public readonly displayName$ = this.select((s) => s.displayName);
  public readonly loading$ = this.select((s) => s.loading);
  public readonly saving$ = this.select((s) => s.saving);
  public readonly error$ = this.select((s) => s.error);
  public readonly loadException$ = this.select((s) => s.loadException);
  public readonly saveException$ = this.select((s) => s.saveException);
  public readonly loadedOriginal$ = this.select((s) => s.loadedOriginal);
  public readonly baselineSignature$ = this.select((s) => s.baselineSignature);

  // Legacy `submit()` parity: `metadata` always carries the label, and folds in
  // the four offsets only for masks; `displayName` rides along only for masks
  // and responses. `id` is the ledger key (placeholder until persisted).
  public readonly draft$ = this.select(
    this.containerId$,
    this.name$,
    this.type$,
    this.version$,
    this.description$,
    this.value$,
    this.file$,
    this.label$,
    this.coordinates$,
    this.displayName$,
    (containerId, name, type, version, description, value, file, label, coordinates, displayName): ResourceEditorDraft => {
      const metadata: Record<string, unknown> =
        type === 'mask'
          ? {
              label,
              offsetXmin: Number(coordinates.offsetXmin),
              offsetYmin: Number(coordinates.offsetYmin),
              offsetXmax: Number(coordinates.offsetXmax),
              offsetYmax: Number(coordinates.offsetYmax),
            }
          : { label };

      return {
        id: containerId ?? '',
        name,
        type: type ?? '',
        version,
        description,
        value,
        metadata,
        displayName: type === 'mask' || type === 'response' ? displayName : undefined,
        file: file ?? undefined,
      };
    }
  );

  // ============================================================================
  // Updaters — fields
  // ============================================================================

  public readonly setName = this.updater((state, name: string) => ({ ...state, name }));
  public readonly setType = this.updater((state, type: string) => ({ ...state, type }));
  public readonly setVersion = this.updater((state, version: number) => ({ ...state, version }));
  public readonly setDescription = this.updater((state, description: string) => ({ ...state, description }));
  public readonly setLabel = this.updater((state, label: string) => ({ ...state, label }));
  public readonly setCoordinates = this.updater((state, coordinates: ResourceEditorCoordinates) => ({
    ...state,
    coordinates,
  }));
  public readonly setDisplayName = this.updater((state, displayName: Record<string, string>) => ({
    ...state,
    displayName,
  }));

  // Picking a file mirrors its name into `value` until the upload resolves (the
  // legacy `updateFile` patched both controls at once).
  public readonly setFile = this.updater((state, payload: { file: File; fileName: string }) => ({
    ...state,
    file: payload.file,
    value: payload.fileName,
  }));

  // ============================================================================
  // Updaters — flags
  // ============================================================================

  public readonly setLoading = this.updater((state, loading: boolean) => ({ ...state, loading }));
  public readonly setSaving = this.updater((state, saving: boolean) => ({ ...state, saving }));
  public readonly setError = this.updater((state, error: string | null) => ({ ...state, error }));

  // Setting an exception transitions out of the corresponding in-flight phase
  // (loading/saving → false) — that's how "load failed" / "save failed" reach
  // the UI. Clearing (null), called proactively at the start of a fresh attempt
  // to wipe a stale error, must NOT touch the phase flag: the surrounding
  // effect has just flipped it to true and we'd otherwise undo that.
  public readonly setLoadException = this.updater((state, loadException: Error | null) => ({
    ...state,
    loadException,
    loading: loadException === null ? state.loading : false,
  }));

  public readonly setSaveException = this.updater((state, saveException: Error | null) => ({
    ...state,
    saveException,
    saving: saveException === null ? state.saving : false,
  }));

  // Seed updaters spread `...INITIAL_STATE`, so the baseline auto-clears on
  // every re-seed — the capture effect must re-run per session.
  public readonly setBaselineSignature = this.updater((state, baselineSignature: string) => ({
    ...state,
    baselineSignature,
  }));

  // ============================================================================
  // Updaters — lifecycle
  // ============================================================================

  public readonly reset = this.updater(() => ({ ...INITIAL_STATE }));

  // Create mode: a pristine session whose tree/ledger key is the placeholder uuid
  // (the persisted `id` stays null until the save cascade mints a real one).
  // `lockedType` pre-seeds and disables the type select — a manager whose catalog
  // is restricted to one type locks creation to that same type.
  public readonly startCreate = this.updater((_state, payload: { placeholderId: string; lockedType?: string }) => ({
    ...INITIAL_STATE,
    coordinates: { ...DEFAULT_COORDINATES },
    containerId: payload.placeholderId,
    type: payload.lockedType ?? INITIAL_STATE.type,
    typeLocked: payload.lockedType !== undefined,
  }));

  public readonly seedFromResource = this.updater((_state, input: RetrieveResourceDetailOutput) => ({
    ...INITIAL_STATE,
    id: input.id,
    containerId: input.id,
    name: input.name,
    type: input.type,
    typeLocked: input.type === 'response',
    version: Number(input.version),
    description: input.description,
    value: input.value,
    label: String(input.metadata['label'] ?? ''),
    coordinates: {
      offsetXmin: Number(input.metadata['offsetXmin'] ?? DEFAULT_COORDINATES.offsetXmin),
      offsetYmin: Number(input.metadata['offsetYmin'] ?? DEFAULT_COORDINATES.offsetYmin),
      offsetXmax: Number(input.metadata['offsetXmax'] ?? DEFAULT_COORDINATES.offsetXmax),
      offsetYmax: Number(input.metadata['offsetYmax'] ?? DEFAULT_COORDINATES.offsetYmax),
    },
    displayName: { ...input.displayName },
    loadedOriginal: {
      id: input.id,
      name: input.name,
      type: input.type,
      description: input.description,
      nestedResourcesCount: input.children.length,
      version: Number(input.version),
      value: input.value,
      displayName: { ...input.displayName },
      metadata: { ...input.metadata },
    },
  }));

  /*
   * Seed an editing session from a staged ledger row (a pending draft, or the
   * override half of an already-edited persisted resource) — in-session staged
   * work outranks the DB. `persistedId` is `null` for pending drafts (their id
   * is still a client placeholder); `original` threads the override's pre-edit
   * half through to `loadedOriginal` so a re-edit keeps the true original.
   */
  public readonly seedFromDraft = this.updater(
    (_state, payload: { draft: ResourceDraftData; persistedId: string | null; original?: ResourceDraftData }) => ({
      ...INITIAL_STATE,
      id: payload.persistedId,
      containerId: payload.draft.id,
      name: payload.draft.name,
      type: payload.draft.type ?? null,
      typeLocked: payload.draft.type === 'response',
      version: Number(payload.draft.version),
      description: payload.draft.description ?? '',
      value: payload.draft.value,
      file: payload.draft.file ?? null,
      label: String(payload.draft.metadata?.['label'] ?? ''),
      coordinates: {
        offsetXmin: Number(payload.draft.metadata?.['offsetXmin'] ?? DEFAULT_COORDINATES.offsetXmin),
        offsetYmin: Number(payload.draft.metadata?.['offsetYmin'] ?? DEFAULT_COORDINATES.offsetYmin),
        offsetXmax: Number(payload.draft.metadata?.['offsetXmax'] ?? DEFAULT_COORDINATES.offsetXmax),
        offsetYmax: Number(payload.draft.metadata?.['offsetYmax'] ?? DEFAULT_COORDINATES.offsetYmax),
      },
      displayName: { ...(payload.draft.displayName ?? {}) },
      loadedOriginal: payload.original ?? null,
    })
  );
}
